import React from "react";
import styles from "./EmployeeTable.module.css";
import { FieldArray, Form, Formik, FormikConfig } from "formik";
import TableTextInput from "./TableTextInput";
import TableSelectInput from "./TableSelectInput";
import TableDateInput from "./TableDateInput";
import { SystemIcon } from "@justworkshr/milo-components";
import FooterButton from "uicomponents/FooterButton";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/CensusAndCurrentInformation/utils/constants";
import { DisplayIcon } from "@justworkshr/milo-components";

export interface IEmployeeCensusFormRow {
  employees?: {
    firstName?: string;
    lastName?: string;
    participantType?: string;
    gender?: string;
    dateOfBirth?: string;
    zip?: string;
    currentPlanName?: string;
    currentEnrollTier?: string;
    currentMonthlyPremium?: string;
    monthlyPremiumPostRenewal?: string;
    uuid?: string;
  }[];
}

interface EmployeeTableForm {
  formikProps: FormikConfig<IEmployeeCensusFormRow>;
  handleBackClick?: () => void;
  handleDelete?: (uuid: string, participantType: string) => void;
  isCobra?: boolean;
  isLoading?: boolean;
  offerHealthInsurance?: boolean;
}
const EmployeeTable = ({ ...props }: EmployeeTableForm): JSX.Element => {
  const MINIMUM_EMPLOYEE_NUMBER = !props.isCobra ? 2 : 1;
  const USE_HEALTH_INSURANCE_COLUMNS = props.offerHealthInsurance;

  return (
    <>
      <div className={styles.helperContainer}>
        <DisplayIcon iconName="announcement" size="medium" color="red" />

        <span>
          <strong className={styles.helperTitle}>
            Remember to group by household
          </strong>
          – employee first with their dependents after.
        </span>
      </div>

      <Formik {...props.formikProps}>
        {({ values }) => (
          <Form>
            <div
              className={styles.container}
              data-testid="employees-table-container"
            >
              <FieldArray
                name="employees"
                render={(arrayhelpers) => {
                  const rows = values?.employees || [];
                  return (
                    <>
                      <section
                        className={styles.employeeTable}
                        data-testid="employees-table"
                      >
                        <header
                          data-testid="employees-table-header"
                          className={styles.headerRow}
                        >
                          <div className={styles.cell}>First name</div>
                          <div className={styles.cell}>Last name</div>
                          <div className={styles.cell}>Participant type</div>
                          <div className={styles.cell}>Sex</div>
                          <div className={styles.cell}>Date of birth</div>
                          <div className={styles.cell}>Home zip code</div>
                          {USE_HEALTH_INSURANCE_COLUMNS && (
                            <>
                              <div
                                data-testid="column-plan-name"
                                className={styles.cell}
                              >
                                Plan name
                              </div>
                              <div
                                data-testid="column-enrollment-tier"
                                className={`${styles.cell} ${styles.enrollmentTier}`}
                              >
                                Current enrollment tier
                              </div>
                              <div
                                data-testid="column-montlhy-premium"
                                className={styles.cell}
                              >
                                Current monthly premium
                              </div>
                              <div
                                data-testid="column-premiuem-after-renewal"
                                className={styles.cell}
                              >
                                Monthly premium after renewal
                              </div>
                            </>
                          )}
                          <div className={`${styles.cell} ${styles.action}`} />
                        </header>
                        {rows.length > 0 ? (
                          rows.map((employeeData: any, index: any) => (
                            <div
                              key={index}
                              className={styles.row}
                              data-testid={`employees-table-row-${index}`}
                            >
                              <div className={styles.cell}>
                                <TableTextInput
                                  name={`employees[${index}].firstName`}
                                />
                              </div>
                              <div className={styles.cell}>
                                <TableTextInput
                                  name={`employees[${index}].lastName`}
                                />
                              </div>
                              <div className={styles.cell}>
                                <TableSelectInput
                                  name={`employees[${index}].participantType`}
                                  options={[
                                    {
                                      value: "Employee",
                                      description: "Employee",
                                    },
                                    {
                                      value: "Spouse",
                                      description: "Spouse",
                                    },
                                    {
                                      value: "Child",
                                      description: "Child",
                                    },
                                  ]}
                                  placeholder="Select"
                                />
                              </div>
                              <div className={styles.cell}>
                                <TableSelectInput
                                  name={`employees[${index}].gender`}
                                  options={[
                                    {
                                      value: "Male",
                                      description: "Male",
                                    },
                                    {
                                      value: "Female",
                                      description: "Female",
                                    },
                                  ]}
                                  placeholder="Select"
                                />
                              </div>
                              <div className={styles.cell}>
                                <TableDateInput
                                  name={`employees[${index}].dateOfBirth`}
                                />
                              </div>
                              <div className={styles.cell}>
                                <TableTextInput
                                  name={`employees[${index}].zip`}
                                />
                              </div>

                              {USE_HEALTH_INSURANCE_COLUMNS && (
                                <>
                                  <div className={styles.cell}>
                                    <TableTextInput
                                      name={`employees[${index}].currentPlanName`}
                                    />
                                  </div>
                                  <div
                                    className={`${styles.cell} ${styles.enrollmentTier}`}
                                  >
                                    <TableSelectInput
                                      name={`employees[${index}].currentEnrollTier`}
                                      options={[
                                        {
                                          value: "Employee-only",
                                          description: "Employee Only",
                                        },
                                        {
                                          value: "Employee-and-child",
                                          description: "Employee and Child",
                                        },
                                        {
                                          value: "Employee-and-family",
                                          description: "Employee and Family",
                                        },
                                        {
                                          value: "Plans-to-waive-benefits",
                                          description:
                                            "Plans to Waive Benefits",
                                        },
                                        {
                                          value: "Refusing-coverage",
                                          description: "Refusing Coverage",
                                        },
                                        {
                                          value: "Not-eligible-for-benefits",
                                          description:
                                            "Not Eligible for Benefits",
                                        },
                                        {
                                          value: "Waiting-period",
                                          description: "Waiting Period",
                                        },
                                      ]}
                                      placeholder="Select"
                                    />
                                  </div>
                                  <div className={styles.cell}>
                                    <TableTextInput
                                      name={`employees[${index}].currentMonthlyPremium`}
                                    />
                                  </div>
                                  <div className={styles.cell}>
                                    <TableTextInput
                                      name={`employees[${index}].monthlyPremiumPostRenewal`}
                                    />
                                  </div>
                                </>
                              )}
                              <div
                                className={`${styles.cell} ${styles.actions}`}
                              >
                                <SystemIcon
                                  iconName="trash"
                                  color="error"
                                  title="delete"
                                  size="small"
                                  className={styles.trashIcon}
                                  onClick={async () => {
                                    const deleteEmp =
                                      await props.handleDelete?.(
                                        employeeData.uuid,
                                        employeeData.participantType
                                      );
                                    if (deleteEmp) {
                                      arrayhelpers.remove(index);
                                      if (rows.length === 1) {
                                        const emptyEmployees = Array(5).fill({
                                          firstName: "",
                                          lastName: "",
                                          participantType: "",
                                          gender: "",
                                          dateOfBirth: "",
                                          zip: "",
                                          currentPlanName: "",
                                          currentEnrollTier: "",
                                          currentMonthlyPremium: "",
                                          monthlyPremiumPostRenewal: "",
                                          uuid: "",
                                        });
                                        emptyEmployees.forEach((item) =>
                                          arrayhelpers.push(item)
                                        );
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <div
                            className={styles.cell}
                            data-testid="employees-table-no-data"
                          ></div>
                        )}
                      </section>
                      <div
                        className={styles.addMoreDiv}
                        data-testid="employees-table-add-more"
                        onClick={() => {
                          const newFiveList = Array(5).fill({
                            firstName: "",
                            lastName: "",
                            participantType: "",
                            gender: "",
                            dateOfBirth: "",
                            zip: "",
                            currentPlanName: "",
                            currentEnrollTier: "",
                            currentMonthlyPremium: "",
                            monthlyPremiumPostRenewal: "",
                            uuid: "",
                          });
                          newFiveList.forEach((item) =>
                            arrayhelpers.push(item)
                          );
                        }}
                      >
                        <SystemIcon
                          iconName="plus-circle"
                          color="brand"
                          title="add"
                          size="small"
                          className={styles.addIcon}
                        />
                        Add 5 more rows
                      </div>
                    </>
                  );
                }}
              />
            </div>
            <FooterButton
              nextButtonText={
                props.isLoading ? LOADING_BUTTON_TEXT : NEXT_BUTTON_TEXT
              }
              backButtonText={BACK_BUTTON_TEXT}
              disabled={
                props.isLoading ||
                (values.employees?.filter(
                  (emp) => emp.participantType === "Employee"
                )?.length ?? 0) < MINIMUM_EMPLOYEE_NUMBER
              }
              handleBackClick={props.handleBackClick}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EmployeeTable;
